import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud, Download, CheckCircle2, RotateCcw, Loader2, RefreshCw, X, Trash2, KanbanSquare, LayoutGrid, Rows3, ChevronLeft, ShieldCheck, Flag, Undo2, Wallet, History } from "lucide-react";
import { API, authHeaders, fileUrl } from "@/constants/roles";
import { VideoPlayer, fmtTime } from "@/components/dashboard/VideoPlayer";
import { PaymentModal } from "@/components/admin/PaymentModal";
import { usePerms } from "@/components/PermsContext";
import { fmtMoney, fmtNum } from "@/utils/currency";

const isVideo = (name = "") => /\.(mp4|mov|webm|mkv|avi|m4v)$/i.test(name);

// Derived pipeline stage for a submission (backend stays the source of truth;
// stages are computed from submission status + job payment + linked video).
const stageOf = (s) => {
  if (s.status === "superseded") return "superseded";
  if (s.status === "awaiting_review") return s.version > 1 ? "resubmitted" : "awaiting";
  if (s.status === "revision_requested") return "revision";
  if (s.status === "manager_approved") return "manager_approved";
  if (s.video_status === "published") return "published";
  if (s.payment_status === "paid") return "completed";
  return "approved";
};

const STAGES = [
  { id: "awaiting", label: "Awaiting Review", style: "bg-amber-500/15 text-amber-300 border-amber-500/30", dot: "bg-amber-400" },
  { id: "resubmitted", label: "Resubmitted", style: "bg-orange-500/15 text-orange-300 border-orange-500/30", dot: "bg-orange-400" },
  { id: "revision", label: "Revision Requested", style: "bg-rose-500/15 text-rose-300 border-rose-500/30", dot: "bg-rose-400" },
  { id: "manager_approved", label: "Manager Approved", style: "bg-violet-500/15 text-violet-300 border-violet-500/30", dot: "bg-violet-400" },
  { id: "approved", label: "Approved", style: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", dot: "bg-emerald-400" },
  { id: "completed", label: "Completed (Paid)", style: "bg-teal-500/15 text-teal-300 border-teal-500/30", dot: "bg-teal-400" },
  { id: "published", label: "Published", style: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", dot: "bg-cyan-400" },
  { id: "superseded", label: "Superseded", style: "bg-[var(--pv-subtle)] text-[var(--pv-text-4)] border-[var(--pv-border)]", dot: "bg-[var(--pv-text-4)]" },
];
const STAGE_MAP = Object.fromEntries(STAGES.map((s) => [s.id, s]));

const inputCls =
  "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-2.5 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

export const AdminSubmissions = () => {
  const { has, role } = usePerms();
  const [subs, setSubs] = useState(null);
  const [view, setView] = useState("pipeline");
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [feedbackFor, setFeedbackFor] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [notes, setNotes] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [payById, setPayById] = useState({});
  const [bonusInputs, setBonusInputs] = useState({});
  const [payModal, setPayModal] = useState(null);
  const [historyOpenFor, setHistoryOpenFor] = useState(null); // job_id with version history expanded
  const vrefs = useRef({});
  const navigate = useNavigate();

  // version history: every upload for a job, newest first
  const versionsByJob = useMemo(() => {
    const map = {};
    for (const s of subs || []) {
      (map[s.job_id] ||= []).push(s);
    }
    for (const list of Object.values(map)) list.sort((a, b) => b.version - a.version);
    return map;
  }, [subs]);

  const getRef = (id) => (vrefs.current[id] ||= { current: null });
  const seekTo = (id, t) => {
    const v = vrefs.current[id]?.current;
    if (v) {
      v.currentTime = t;
      v.play().catch(() => {});
    }
  };

  const load = useCallback(() => {
    axios
      .get(`${API}/admin/submissions`, { headers: authHeaders() })
      .then((r) => setSubs(r.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
        else toast.error("Could not load submissions.");
      });
    // payment records are the single source of truth for base/bonus/total on approved work —
    // keyed by payment id (the backend resolves THE correct payment per job; keying client-side
    // by job_id could open the wrong record when a job has more than one payment)
    if (role === "admin") {
      axios
        .get(`${API}/admin/payments`, { headers: authHeaders() })
        .then((r) => setPayById(Object.fromEntries((r.data || []).map((p) => [p.id, p]))))
        .catch(() => toast.error("Could not load payment records — Review / mark paid may be unavailable. Try Refresh."));
    }
  }, [navigate, role]);

  useEffect(load, [load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      const bonus = parseFloat(bonusInputs[id] || "0") || 0;
      await axios.post(`${API}/admin/submissions/${id}/approve`, bonus ? { bonus } : {}, { headers: authHeaders() });
      toast.success(bonus ? `Approved with ${fmtMoney(bonus)} bonus — payment queued.` : "Approved — job completed, payment queued, video added to the library.");
      setBonusInputs((b) => ({ ...b, [id]: "" }));
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not approve.");
    } finally {
      setBusyId(null);
    }
  };

  const requestRevision = async (id) => {
    if (feedback.trim().length < 3) {
      toast.error("Add revision notes first.");
      return;
    }
    setBusyId(id);
    try {
      await axios.post(
        `${API}/admin/submissions/${id}/request-revision`,
        { feedback: feedback.trim(), notes: notes.filter((n) => n.text.trim()) },
        { headers: authHeaders() }
      );
      toast.success("Revision requested — the worker will see your notes.");
      setFeedbackFor(null);
      setFeedback("");
      setNotes([]);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not request revision.");
    } finally {
      setBusyId(null);
    }
  };

  const deleteSubmission = async (s) => {    setBusyId(s.id);
    try {
      const { data } = await axios.delete(`${API}/admin/submissions/${s.id}`, { headers: authHeaders() });
      toast.success(
        data.removed_payments > 0
          ? `Deleted — ${data.removed_payments} payment record(s) reversed and the job is active again.`
          : "Submission deleted."
      );
      setConfirmDelete(null);
      setSelectedId(null);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not delete.");
    } finally {
      setBusyId(null);
    }
  };

  const managerApprove = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/submissions/${id}/manager-approve`, {}, { headers: authHeaders() });
      toast.success("Manager approved — sent to the owner for final approval.");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not approve.");
    } finally {
      setBusyId(null);
    }
  };

  const reopenReview = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/submissions/${id}/reopen-review`, {}, { headers: authHeaders() });
      toast.success("Sent back to the review queue.");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not reopen.");
    } finally {
      setBusyId(null);
    }
  };

  const [flagFor, setFlagFor] = useState(null);
  const [flagForm, setFlagForm] = useState({ reason: "", note: "", priority: "normal" });
  const sendFlag = async (s) => {
    if (flagForm.reason.trim().length < 3) {
      toast.error("Give the owner a reason.");
      return;
    }
    setBusyId(s.id);
    try {
      await axios.post(`${API}/staff/flags`, {
        target_type: "submission", target_id: s.id,
        reason: flagForm.reason.trim(), note: flagForm.note.trim(), priority: flagForm.priority,
      }, { headers: authHeaders() });
      toast.success("Flagged for the owner's attention.");
      setFlagFor(null);
      setFlagForm({ reason: "", note: "", priority: "normal" });
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not flag.");
    } finally {
      setBusyId(null);
    }
  };

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((st) => [st.id, []]));
    (subs || []).forEach((s) => map[stageOf(s)].push(s));
    return map;
  }, [subs]);

  const visible = (subs || []).filter((s) => filter === "all" || stageOf(s) === filter);
  const selected = selectedId ? (subs || []).find((s) => s.id === selectedId) : null;

  const stageBadge = (s) => {
    const st = STAGE_MAP[stageOf(s)];
    return (
      <span data-testid={`submission-status-${s.id}`} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${st.style}`}>
        {st.label}
      </span>
    );
  };

  // Full review card — player, notes, actions. Shared by every view.
  const renderCard = (s, { detail = false } = {}) => (
    <article key={s.id} data-testid={`submission-card-${s.id}`} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h3 className="font-display text-lg font-semibold text-[var(--pv-text)]">{s.job_title || "Untitled job"}</h3>
            <span className="rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-raised)] px-2.5 py-0.5 text-[11px] font-bold text-[var(--pv-text-2)]">v{s.version}</span>
            {(versionsByJob[s.job_id] || [])[0]?.id === s.id && (versionsByJob[s.job_id] || []).length > 1 && (
              <span data-testid={`submission-current-${s.id}`} className="rounded-full border border-cyan-500/30 bg-cyan-500/15 px-2 py-0.5 text-[9px] font-bold uppercase text-cyan-300">Current — latest</span>
            )}
            {stageBadge(s)}
            {s.payment_status && (
              <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${s.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
                {s.payment_status}
              </span>
            )}
          </div>
          <p className="mt-1.5 text-xs font-medium text-[var(--pv-text-4)]">
            By <span className="font-bold text-[var(--pv-text-2)]">{s.worker_name || "Unknown"}</span>
            <span className="mx-2 text-[var(--pv-text-2)]">|</span>
            {s.role_required}
            <span className="mx-2 text-[var(--pv-text-2)]">|</span>
            {fmtMoney(s.estimated_pay ?? 0)}
            <span className="mx-2 text-[var(--pv-text-2)]">|</span>
            {new Date(s.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            data-testid={`submission-download-${s.id}`}
            href={fileUrl(s.file_path)}
            download={s.filename}
            className="flex items-center gap-1.5 rounded-full bg-[var(--pv-raised)] px-4 py-2 text-xs font-bold text-[var(--pv-text)] transition-colors hover:bg-[var(--pv-raised)]"
          >
            <Download className="h-3.5 w-3.5" /> {s.filename}
          </a>
          {!has("delete_records") ? null : confirmDelete === s.id ? (
            <span data-testid={`submission-delete-confirm-${s.id}`} className="flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1.5">
              <span className="text-[11px] font-bold text-rose-300">Delete permanently? Payments get reversed.</span>
              <button data-testid={`sub-delete-confirm-btn-${s.id}`} onClick={() => deleteSubmission(s)} disabled={busyId === s.id} className="rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-rose-600 disabled:opacity-60">
                {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes, delete"}
              </button>
              <button onClick={() => setConfirmDelete(null)} className="text-[11px] font-bold text-[var(--pv-text-3)]">Cancel</button>
            </span>
          ) : (
            <button data-testid={`sub-delete-btn-${s.id}`} onClick={() => setConfirmDelete(s.id)} title="Delete submission (reverses payments)" className="flex items-center gap-1.5 rounded-full border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-xs font-bold text-rose-400 transition-colors hover:bg-rose-500/20">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {(versionsByJob[s.job_id] || []).length > 1 && (
        <div data-testid={`version-history-admin-${s.job_id}`} className="mt-4 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
          <button
            data-testid={`version-history-toggle-${s.job_id}`}
            onClick={() => setHistoryOpenFor((cur) => (cur === s.job_id ? null : s.job_id))}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--pv-text-3)] transition-colors hover:text-cyan-400"
          >
            <History className="h-3.5 w-3.5" /> Version history ({versionsByJob[s.job_id].length})
          </button>
          {historyOpenFor === s.job_id && (
            <ul className="mt-3 space-y-2">
              {versionsByJob[s.job_id].map((v, i) => (
                <li key={v.id} data-testid={`version-row-${v.id}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2.5">
                  <span className="rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-raised)] px-2 py-0.5 text-[10px] font-bold text-[var(--pv-text-2)]">v{v.version}</span>
                  <span className="text-xs font-bold text-[var(--pv-text-2)]">{v.version === 1 ? "Original submission" : "Revised submission"}</span>
                  {i === 0 && (
                    <span className="rounded-full border border-cyan-500/30 bg-cyan-500/15 px-2 py-0.5 text-[9px] font-bold uppercase text-cyan-300">Current — latest</span>
                  )}
                  <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase ${STAGE_MAP[stageOf(v)]?.style || ""}`}>{STAGE_MAP[stageOf(v)]?.label || v.status}</span>
                  <span className="text-[10px] font-medium text-[var(--pv-text-4)]">
                    {new Date(v.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </span>
                  {v.note && <span className="w-full text-[11px] italic text-[var(--pv-text-4)] sm:w-auto sm:flex-1">Worker note: “{v.note}”</span>}
                  <a data-testid={`version-download-${v.id}`} href={fileUrl(v.file_path)} download={v.filename} className="ml-auto flex items-center gap-1 text-[11px] font-bold text-cyan-400 hover:text-cyan-300">
                    <Download className="h-3 w-3" /> {v.filename}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-4">
        {isVideo(s.filename) ? (
          <VideoPlayer
            path={s.file_path}
            filename={s.filename}
            testid={`submission-player-${s.id}`}
            videoRef={getRef(s.id)}
            onCapture={feedbackFor === s.id ? (t) => setNotes((ns) => [...ns, { t, text: "" }]) : undefined}
          />
        ) : (
          <p className="rounded-2xl bg-[var(--pv-subtle)] px-4 py-3 text-xs text-[var(--pv-text-4)]">Preview not available for this file type — use the download button above.</p>
        )}
      </div>

      {s.note && <p className="mt-4 rounded-2xl bg-[var(--pv-subtle)] px-4 py-3 text-sm text-[var(--pv-text-4)]"><span className="font-bold text-[var(--pv-text-2)]">Worker note: </span>{s.note}</p>}
      {s.feedback && <p className="mt-4 rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300"><span className="font-bold">Revision notes: </span>{s.feedback}</p>}
      {s.revision_notes?.length > 0 && (
        <div data-testid={`revision-notes-${s.id}`} className="mt-3 space-y-2">
          {s.revision_notes.map((n, i) => (
            <div key={i} className="flex items-start gap-2.5 rounded-xl border border-rose-500/20 bg-rose-500/5 px-3 py-2">
              <button
                type="button"
                data-testid={`revision-note-seek-${s.id}-${i}`}
                onClick={() => seekTo(s.id, n.t)}
                className="mt-0.5 shrink-0 rounded-full border border-cyan-500/30 bg-cyan-500/15 px-2 py-0.5 font-mono-label text-[10px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/25"
              >
                {fmtTime(n.t)}
              </button>
              <p className="text-xs leading-relaxed text-rose-200">{n.text}</p>
            </div>
          ))}
        </div>
      )}

      {(s.status === "awaiting_review" || s.status === "manager_approved") && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {has("final_approve") && (
            <>
              <input
                data-testid={`approve-bonus-${s.id}`}
                type="number"
                min="0"
                step="0.01"
                value={bonusInputs[s.id] || ""}
                onChange={(e) => setBonusInputs((b) => ({ ...b, [s.id]: e.target.value }))}
                placeholder="Bonus $ (optional)"
                className="w-32 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3.5 py-2 text-xs font-bold text-[var(--pv-text)] placeholder:font-medium placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
              />
              <button
                data-testid={`approve-btn-${s.id}`}
                onClick={() => approve(s.id)}
                disabled={busyId === s.id}
                className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white shadow-emerald-500/25 transition-colors hover:bg-emerald-600 disabled:opacity-60"
              >
                {busyId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                {s.status === "manager_approved" ? "Final approve & queue payment" : "Approve & queue payment"}
              </button>
            </>
          )}
          {s.status === "awaiting_review" && !has("final_approve") && has("manager_approve") && (
            <button
              data-testid={`manager-approve-btn-${s.id}`}
              onClick={() => managerApprove(s.id)}
              disabled={busyId === s.id}
              className="flex items-center gap-1.5 rounded-full bg-violet-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-violet-600 disabled:opacity-60"
            >
              {busyId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
              Manager approve — send to owner
            </button>
          )}
          {s.status === "manager_approved" && (
            <span className="flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 px-3 py-2 text-[11px] font-bold text-violet-300">
              <ShieldCheck className="h-3.5 w-3.5" /> Quality approved — waiting for owner
            </span>
          )}
          {s.status === "manager_approved" && has("manager_approve") && (
            <button
              data-testid={`reopen-btn-${s.id}`}
              onClick={() => reopenReview(s.id)}
              disabled={busyId === s.id}
              className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] transition-colors hover:text-[var(--pv-text)] disabled:opacity-60"
            >
              <Undo2 className="h-3.5 w-3.5" /> Back to review queue
            </button>
          )}
          {has("request_revisions") && (
            <button
              data-testid={`revision-btn-${s.id}`}
              onClick={() => { setFeedbackFor(feedbackFor === s.id ? null : s.id); setFeedback(""); setNotes([]); }}
              className="flex items-center gap-1.5 rounded-full border border-rose-500/20 bg-rose-500/10 px-4 py-2 text-xs font-bold text-rose-400 transition-colors hover:bg-rose-500/15"
            >
              {feedbackFor === s.id ? <X className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
              Request revision
            </button>
          )}
          <button
            data-testid={`flag-btn-${s.id}`}
            onClick={() => { setFlagFor(flagFor === s.id ? null : s.id); setFlagForm({ reason: "", note: "", priority: "normal" }); }}
            className="flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-4 py-2 text-xs font-bold text-amber-400 transition-colors hover:bg-amber-500/15"
          >
            <Flag className="h-3.5 w-3.5" /> Flag for owner
          </button>
        </div>
      )}

      {/* Payment card — approved work. Payment record is the single source of truth:
          base + bonus = total drives budget, earnings and history everywhere. The backend
          resolves THE payment for this job (payment_id) — the button always opens that same
          record, even if the local payments list hasn't loaded. */}
      {role === "admin" && s.status === "approved" && (() => {
        const pay = (s.payment_id && payById[s.payment_id]) || null;
        if (!s.payment_id) return null; // no payment record exists for this job (legacy/reversed)
        if (!pay) {
          return (
            <div data-testid={`submission-payment-${s.id}`} className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/5 p-4">
              <span className="text-xs text-[var(--pv-text-4)]">Payment record exists — open it directly.</span>
              <button
                data-testid={`submission-review-payment-${s.id}`}
                onClick={() => setPayModal({ id: s.payment_id, job_title: s.job_title, worker_id: s.worker_id })}
                className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white shadow-lg shadow-emerald-500/25 transition-colors hover:bg-emerald-600"
              >
                <Wallet className="h-3.5 w-3.5" /> Review / mark paid
              </button>
            </div>
          );
        }
        return (
          <div data-testid={`submission-payment-${s.id}`} className={`mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 ${pay.status === "paid" ? "border-emerald-500/25 bg-emerald-500/5" : "border-amber-500/25 bg-amber-500/5"}`}>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs">
              <span className="text-[var(--pv-text-4)]">Base <span className="font-bold text-[var(--pv-text)]">{fmtMoney(pay.base)}</span></span>
              <span className="text-[var(--pv-text-4)]">Bonus <span className="font-bold text-[var(--pv-text)]">{fmtMoney(pay.bonus || 0)}</span></span>
              <span className="text-[var(--pv-text-4)]">{pay.status === "paid" ? "Total paid" : "Total owed"} <span className={`font-display text-sm font-bold ${pay.status === "paid" ? "text-emerald-400" : "text-amber-300"}`}>{fmtMoney(pay.amount)}</span></span>
              {pay.status === "paid" && pay.method && <span className="text-[var(--pv-text-4)]">via <span className="font-bold text-[var(--pv-text-2)]">{pay.method}</span></span>}
              {pay.status === "paid" && pay.paid_at && <span className="text-[var(--pv-text-4)]">{new Date(pay.paid_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>}
              {pay.has_proof && <span className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300">Proof attached</span>}
            </div>
            <button
              data-testid={`submission-review-payment-${s.id}`}
              onClick={() => setPayModal(pay)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${pay.status === "paid" ? "border border-[var(--pv-border)] text-[var(--pv-text-2)] hover:bg-[var(--pv-subtle)]" : "bg-emerald-500 text-white shadow-lg shadow-emerald-500/25 hover:bg-emerald-600"}`}
            >
              <Wallet className="h-3.5 w-3.5" /> {pay.status === "paid" ? "View payment & proof" : "Review / mark paid"}
            </button>
          </div>
        );
      })()}

      {flagFor === s.id && (
        <div data-testid={`flag-form-${s.id}`} className="mt-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-300">Needs owner attention</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_140px]">
            <input
              data-testid={`flag-reason-${s.id}`}
              value={flagForm.reason}
              onChange={(e) => setFlagForm((f) => ({ ...f, reason: e.target.value }))}
              placeholder="Reason (e.g. quality concern, deadline risk)"
              className={inputCls}
            />
            <select data-testid={`flag-priority-${s.id}`} value={flagForm.priority} onChange={(e) => setFlagForm((f) => ({ ...f, priority: e.target.value }))} className={inputCls}>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>
          <input
            data-testid={`flag-note-${s.id}`}
            value={flagForm.note}
            onChange={(e) => setFlagForm((f) => ({ ...f, note: e.target.value }))}
            placeholder="Optional note for the owner"
            className={`${inputCls} mt-3`}
          />
          <button data-testid={`flag-submit-${s.id}`} onClick={() => sendFlag(s)} disabled={busyId === s.id} className="mt-3 flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-xs font-bold text-white hover:bg-amber-600 disabled:opacity-60">
            {busyId === s.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Send to owner
          </button>
        </div>
      )}

      {feedbackFor === s.id && (
        <div className="mt-4 rounded-2xl border border-rose-500/20 bg-rose-500/10 p-4">
          <label htmlFor={`feedback-${s.id}`} className="mb-1.5 block text-xs font-semibold text-rose-300">Revision notes (the worker sees exactly this)</label>
          <textarea
            id={`feedback-${s.id}`}
            data-testid={`revision-feedback-${s.id}`}
            rows={3}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder="Overall feedback — what needs to change?"
            className="w-full rounded-2xl border border-rose-500/20 bg-[var(--pv-card)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-rose-400 focus:outline-none focus:ring-4 focus:ring-rose-500/10"
          />

          <div className="mt-4 border-t border-rose-500/20 pt-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-rose-300/80">Timestamped notes</p>
            <p className="mt-1 text-xs text-rose-200/60">While watching the preview above, hit <span className="font-bold text-cyan-300">"Note at 0:00"</span> in the player bar to pin a note to that exact moment.</p>
            {notes.length > 0 && (
              <div data-testid={`revision-notes-editor-${s.id}`} className="mt-3 space-y-2">
                {notes.map((n, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`note-seek-${s.id}-${i}`}
                      onClick={() => seekTo(s.id, n.t)}
                      className="shrink-0 rounded-full border border-cyan-500/30 bg-cyan-500/15 px-2.5 py-1 font-mono-label text-[10px] font-bold text-cyan-300 transition-colors hover:bg-cyan-500/25"
                    >
                      {fmtTime(n.t)}
                    </button>
                    <input
                      data-testid={`note-text-${s.id}-${i}`}
                      value={n.text}
                      onChange={(e) => setNotes((ns) => ns.map((x, xi) => (xi === i ? { ...x, text: e.target.value } : x)))}
                      placeholder="What needs fixing at this moment?"
                      className="min-w-0 flex-1 rounded-xl border border-rose-500/20 bg-[var(--pv-card)] px-3 py-2 text-xs font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-rose-400 focus:outline-none"
                    />
                    <button
                      type="button"
                      data-testid={`note-remove-${s.id}-${i}`}
                      onClick={() => setNotes((ns) => ns.filter((_, xi) => xi !== i))}
                      className="shrink-0 rounded-full bg-rose-500/15 p-1.5 text-rose-400 transition-colors hover:bg-rose-500/25"
                      aria-label="Remove note"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            data-testid={`revision-submit-${s.id}`}
            onClick={() => requestRevision(s.id)}
            disabled={busyId === s.id}
            className="mt-4 flex items-center gap-1.5 rounded-full bg-rose-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-rose-600 disabled:opacity-60"
          >
            {busyId === s.id && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Send revision request
          </button>
        </div>
      )}
    </article>
  );

  // Compact card for pipeline columns
  const renderMini = (s) => (
    <button
      key={s.id}
      data-testid={`pipeline-card-${s.id}`}
      onClick={() => setSelectedId(s.id)}
      className={`w-full rounded-2xl border p-3.5 text-left transition-all hover:-translate-y-0.5 ${selectedId === s.id ? "border-cyan-500/50 bg-cyan-500/5" : "border-[var(--pv-border)] bg-[var(--pv-card)] hover:border-cyan-500/30"}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-xs font-bold text-[var(--pv-text)]">{s.job_title || "Untitled job"}</p>
        <span className="shrink-0 rounded-full border border-[var(--pv-border-strong)] bg-[var(--pv-raised)] px-1.5 py-0.5 text-[9px] font-bold text-[var(--pv-text-2)]">v{s.version}</span>
      </div>
      <p className="mt-1 truncate text-[11px] text-[var(--pv-text-4)]">{s.worker_name || "Unknown"} · {fmtMoney(s.estimated_pay ?? 0, 0)}</p>
      <p className="mt-0.5 text-[10px] text-[var(--pv-text-4)]">{new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</p>
    </button>
  );

  // Detail view (pipeline / table selection)
  if (selected) {
    return (
      <div data-testid="submission-detail-view">
        <button data-testid="submission-detail-back" onClick={() => { setSelectedId(null); setConfirmDelete(null); setFeedbackFor(null); }} className="mb-4 flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2 text-xs font-semibold text-[var(--pv-text-2)] hover:text-[var(--pv-text)]">
          <ChevronLeft className="h-3.5 w-3.5" /> Back to submissions
        </button>
        {renderCard(selected, { detail: true })}
      </div>
    );
  }

  return (
    <div data-testid="admin-submissions">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Submissions</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">The production pipeline — from first delivery to published video.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] p-1">
            <button data-testid="subs-view-pipeline" onClick={() => setView("pipeline")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === "pipeline" ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)]"}`}><KanbanSquare className="h-3.5 w-3.5" /> Pipeline</button>
            <button data-testid="subs-view-cards" onClick={() => setView("cards")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === "cards" ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)]"}`}><LayoutGrid className="h-3.5 w-3.5" /> Cards</button>
            <button data-testid="subs-view-table" onClick={() => setView("table")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${view === "table" ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)]"}`}><Rows3 className="h-3.5 w-3.5" /> Table</button>
          </div>
          <button
            data-testid="submissions-refresh-btn"
            onClick={load}
            className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-4)] transition-colors hover:bg-[var(--pv-subtle)]"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {!subs ? (
        <p data-testid="submissions-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading submissions...</p>
      ) : subs.length === 0 ? (
        <div data-testid="submissions-empty" className="mt-6 flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
          <UploadCloud className="h-10 w-10 text-[var(--pv-text-2)]" />
          <p className="text-sm font-medium text-[var(--pv-text-4)]">Nothing here right now.</p>
        </div>
      ) : view === "pipeline" ? (
        <div data-testid="submissions-pipeline" className="mt-6 flex gap-3 overflow-x-auto pb-4">
          {STAGES.map((st) => (
            <div key={st.id} data-testid={`pipeline-col-${st.id}`} className="flex w-64 shrink-0 flex-col rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-bg)] p-3">
              <div className="flex items-center gap-2 px-2 pb-3 pt-1">
                <span className={`h-2 w-2 rounded-full ${st.dot}`} />
                <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-3)]">{st.label}</p>
                <span className="ml-auto rounded-full bg-[var(--pv-subtle)] px-2 py-0.5 text-[10px] font-bold text-[var(--pv-text-3)]">{byStage[st.id].length}</span>
              </div>
              <div className="space-y-2">
                {byStage[st.id].length === 0 ? (
                  <p className="rounded-2xl border border-dashed border-[var(--pv-border)] px-3 py-6 text-center text-[11px] text-[var(--pv-text-4)]">Empty</p>
                ) : (
                  byStage[st.id].map(renderMini)
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="mt-6 flex flex-wrap gap-2">
            {[{ id: "all", label: "All" }, ...STAGES].map((f) => (
              <button
                key={f.id}
                data-testid={`sub-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                  filter === f.id ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)] hover:border-cyan-500/30"
                }`}
              >
                {f.label}
                <span className="ml-1.5 opacity-70">{f.id === "all" ? subs.length : byStage[f.id].length}</span>
              </button>
            ))}
          </div>

          {visible.length === 0 ? (
            <div data-testid="submissions-filtered-empty" className="mt-6 flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
              <UploadCloud className="h-10 w-10 text-[var(--pv-text-2)]" />
              <p className="text-sm font-medium text-[var(--pv-text-4)]">Nothing in this stage right now.</p>
            </div>
          ) : view === "cards" ? (
            <div className="mt-6 space-y-4">{visible.map((s) => renderCard(s))}</div>
          ) : (
            <div data-testid="submissions-table" className="mt-6 overflow-x-auto rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
              <table className="w-full min-w-[860px] text-left text-xs">
                <thead>
                  <tr className="border-b border-[var(--pv-border)] text-[10px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">
                    <th className="px-5 py-3">Job</th><th className="px-4 py-3">Worker</th><th className="px-4 py-3">Ver</th><th className="px-4 py-3">Stage</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3">Pay</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((s) => (
                    <tr key={s.id} data-testid={`submission-row-${s.id}`} onClick={() => setSelectedId(s.id)} className="cursor-pointer border-b border-[var(--pv-border)] last:border-0 hover:bg-[var(--pv-subtle)]">
                      <td className="max-w-[220px] truncate px-5 py-3 font-bold text-[var(--pv-text)]">{s.job_title || "Untitled job"}</td>
                      <td className="px-4 py-3 text-[var(--pv-text-3)]">{s.worker_name || "Unknown"}</td>
                      <td className="px-4 py-3 text-[var(--pv-text-3)]">v{s.version}</td>
                      <td className="px-4 py-3">{stageBadge(s)}</td>
                      <td className="px-4 py-3 text-[var(--pv-text-3)]">{s.payment_status || "—"}</td>
                      <td className="px-4 py-3 text-[var(--pv-text-2)]">{fmtMoney(s.estimated_pay ?? 0)}</td>
                      <td className="px-4 py-3 text-[var(--pv-text-4)]">{new Date(s.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</td>
                      <td className="px-4 py-3 text-right">
                        <button data-testid={`submission-review-btn-${s.id}`} onClick={(e) => { e.stopPropagation(); setSelectedId(s.id); }} className="text-[10px] font-bold uppercase text-cyan-400 transition-colors hover:text-cyan-300">Review</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {payModal && (
        <PaymentModal payment={payModal} onClose={() => setPayModal(null)} onChanged={load} />
      )}
    </div>
  );
};
